import { HttpErrorResponse, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { catchError, firstValueFrom, Observable, switchMap, take, throwError } from "rxjs";
import { Injectable } from "@angular/core";
import { SessionService } from "../services/session/session.service";
import { AuthService } from "@auth0/auth0-angular";
import { environment } from "environments/environment";
import { STORAGE_PRESIGN_BATCH_PATH } from "../services/storage/storage.service";

@Injectable({ providedIn: 'root' })
export class AuthInterceptor implements HttpInterceptor {

    constructor(private authService: AuthService, private sessionService: SessionService) {
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
        const publicUrls = [`${environment.apiMicroservice}products/`, `${environment.apiMicroservice}aws/design/mockup/files`, `${environment.apiMicroservice}marketplace/pricing/design-tool`, `${environment.apiRoot}addresses/create_and_verify`, `${environment.apiRoot}shipments`, `${environment.apiUrl2}api/v2/order/client-portal/approvals`, `${environment.apiUrl2}api/v2/cloud/platform/art-approval/`, `${environment.apiUrl2}api/v2/cloud/platform/photo-approval/`, 'https://img-art.stokkup.com/', `${environment.apiUrl2}api/v2/feedback/`, '/api/img/', `${environment.apiRoot}client-portal/art/download-art-approval`, `${environment.apiUrl}art/approve/`, `${environment.apiUrl}art/feedback/`, `${environment.apiMicroservice}marketplace/marketing/sync`, `${environment.apiMicroservice}legacy/order/cp/`, `${environment.apiMicroservice}legacy/v2/order-details`, `/print-sequence`, `${environment.apiMicroservice}aws/storage/presign-batch`]

        if (publicUrls.some(e => request.url.includes(e))) {
            return next.handle(request)
        }

        /**
         * OPTIONAL-AUTH urls (CS-4493). Sent WITH the Auth0 token when there is a
         * session, and ANONYMOUSLY when there is not — rather than failing.
         *
         * Why this is not just another `publicUrls` entry: `publicUrls` short-circuits
         * for every caller, so listing presign there would also strip the token from
         * authenticated requests, which currently work in production. This keeps the
         * authenticated path byte-for-byte unchanged and only adds a guest path.
         *
         * Context: the S3 presign endpoint backs the Art Approval artwork on
         * `/orders/:id/art` and the Order Detail "Final artwork files" card, both of
         * which are reachable by guests via emailed live links (GuestOrAuthGuard).
         * Without this, `getAccessTokenSilently()` rejects with "Missing Refresh
         * Token", the request never leaves the browser, and guests get no artwork.
         *
         * ⚠️ SECURITY: this makes presign callable without credentials. The gateway
         * therefore cannot identify the caller and must authorize on the requested
         * key alone. Keys are `{companyId}/order_design_position/{positionId}/{file}`
         * — companyId and positionId are sequential and guessable, so the filename is
         * the only unguessable component. Accepted deliberately to unblock guest live
         * links; the durable fix is a token-scoped presign endpoint
         * (docs/art-approval-images-backend-spec.md §4.1), after which this entry
         * should be removed.
         */
        const optionalAuthUrls = [`${environment.apiMicroservice}${STORAGE_PRESIGN_BATCH_PATH}`]

        if (optionalAuthUrls.some(e => request.url.includes(e))) {
            // take(1): Auth0's observables never complete, so without it the outer
            // switchMap would keep the request stream open forever.
            return this.authService.isAuthenticated$.pipe(
                take(1),
                switchMap(isAuthenticated => isAuthenticated
                    ? this.withAuth0Token(request, next)
                    : next.handle(request))
            )
        }

        return this.withAuth0Token(request, next)
    }

    /**
     * The original authenticated request path, extracted verbatim so that
     * {@link intercept} can reuse it for optional-auth urls. Behaviour is unchanged:
     * resolve the Auth0 user and token, attach the CP-* headers (plus `Authorization`
     * for protected hosts), and log the caller out on a 401 or a missing refresh token.
     */
    private withAuth0Token(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
        const protectedUrls = [environment.apiRoot, environment.apiUrl, environment.apiUrl2, environment.apiMicroservice]

        return this.authService.user$.pipe(
            switchMap(currentUser => {
                return this.authService.getAccessTokenSilently().pipe(
                    switchMap(token => {
                        const activeClient = this.sessionService.getActiveClient();
                        const activeDecorator = this.sessionService.getActiveDecorator();
                        
                        if (currentUser) {
                            let headers = new HttpHeaders({
                                'CP-User-Id': currentUser?.cpu_id?.toString(),
                                'CP-User-Email': currentUser?.email,
                                'CP-User-Token': 'Bearer ' + token,
                                'CP-Affiliate': currentUser?.is_affiliate_user ?? 0,
                            })

                            if (protectedUrls.some(e => request.url.includes(e))) {
                                headers = headers.append('Authorization', 'Bearer ' + token)
                            }

                            if (activeClient?.id) {
                                headers = headers.append('CP-Client-Id', `${activeClient.id}`);
                            }

                            if (activeDecorator?.id) {
                                headers = headers.append('CP-Decorator-Id', `${activeDecorator.id}`);
                            }

                            request = request.clone({ headers });
                        }

                        return next.handle(request).pipe(
                            catchError(async (error: HttpErrorResponse) => {
                                console.error('an error happened while doing a request', error)
                                if (error.status === 401) {
                                    console.error('an authentication error occurred trying to get data, login you out')
                                    const isAuthenticated = await firstValueFrom(this.authService.isAuthenticated$);
                                    if (isAuthenticated) {
                                        this.authService.logout({
                                            openUrl: (url) => {
                                                this.sessionService.flushUser();
                                                window.location.replace(url);
                                            },
                                            logoutParams: {
                                                returnTo: window.location.origin,
                                                federated: true
                                            }
                                        }).subscribe();
                                    }
                                }
                                return throwError(() => error);
                            })
                        );
                    }),
                    catchError(async (error: HttpErrorResponse) => {
                        const errorMsg = error.toString()
                        if (errorMsg.includes('Missing Refresh Token') || errorMsg.includes('Unknown or invalid refresh token')) {
                            console.error(request.url + ': an error was found trying to get a token, login you out')
                            const isAuthenticated = await firstValueFrom(this.authService.isAuthenticated$);
                            if (isAuthenticated) {
                                this.authService.logout({
                                    openUrl: (url) => {
                                        this.sessionService.flushUser();
                                        window.location.replace(url);
                                    },
                                    logoutParams: {
                                        returnTo: window.location.origin,
                                        federated: true
                                    }
                                }).subscribe();
                            }
                        }
                        return throwError(() => error);
                    })
                )
            })
        );
    }
}
